import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/definition"
import {
    determineDescriptor,
    determineSourceImageMipLevelRegion,
    determineTargetImageRegion,
} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/helpers"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {HalImage} from "@cm/hal"
import {getHalAddressMode} from "@cm/webgl2/utils"
import {isBox2Like, Vector2Like} from "@cm/math"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof Definition

export class ImageOpExtractRegionHal extends ImageOpHal<Definition, ImageOpExtractRegionHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpExtractRegionHalData>): ImageOpExtractRegionHalData {
        return new ImageOpExtractRegionHalData({
            ...args,
            descriptor: determineDescriptor(args.parameters.sourceImage.descriptor, args.parameters.region),
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpExtractRegionHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpExtractRegionHalData>) {
        super(args)
        this.painter =
            args.prevData?.painter.addRef() ??
            this.ctx.createPainter(
                "compositor",
                `
            uniform int u_useOffsetImage;
            uniform vec2 u_sourceOffset;
            uniform int u_sourceMipLevel;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                vec2 offset = u_sourceOffset;
                if (u_useOffsetImage != 0) {
                    offset += texelFetch1(ivec2(0, 0)).xy / float(1 << u_sourceMipLevel);
                }
                return textureLodPx0(vec2(targetPixel) + offset + 0.5, u_sourceMipLevel, u_addressMode);
            }
            `,
            )
    }

    protected override dispose() {
        this.painter.release()
        super.dispose()
    }

    protected override async updateHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        // TODO currently we can only use a view if the region is aligned to the mip level and within the source image bounds; this can be relaxed once the views deal with unaligned pixels in higher mip-levels properly !
        const mipLevelPixelSize = 1 << mipLevelRegion.mipLevel
        const allowViews = true
        const sourceMipLevelSize = getMipLevelSize(this.parameters.sourceImage.descriptor, mipLevelRegion.mipLevel + (this.parameters.mipLevelOffset ?? 0))
        if (
            allowViews &&
            isBox2Like(this.parameters.region) &&
            this.parameters.region.x >= 0 &&
            this.parameters.region.y >= 0 &&
            this.parameters.region.x + this.parameters.region.width <= sourceMipLevelSize.width &&
            this.parameters.region.y + this.parameters.region.height <= sourceMipLevelSize.height &&
            this.parameters.region.x % mipLevelPixelSize === 0 &&
            this.parameters.region.y % mipLevelPixelSize === 0 &&
            this.parameters.region.width % mipLevelPixelSize === 0 &&
            this.parameters.region.height % mipLevelPixelSize === 0
        ) {
            // we can use a view of the source image
            const sourceMipLevelRegion = determineSourceImageMipLevelRegion(
                this.parameters.sourceImage.descriptor,
                this.parameters.region,
                this.parameters.mipLevelOffset,
                mipLevelRegion,
            )
            const sourceHalImage = await this.parameters.sourceImage.getUpdatedHalImage(sourceMipLevelRegion, options)
            return RegionLockedHalImageRef.makeView(sourceHalImage, this.parameters.region, this.parameters.mipLevelOffset)
        } else {
            return super.updateHalImage(mipLevelRegion, options)
        }
    }

    // ImageHal
    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, region, mipLevelOffset: mipLevelOffsetIn, addressMode} = this.parameters
        sourceImage.addRef("ExtractRegion.writeRegion - sourceImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined
        let updatedOffsetImage: RegionLockedHalImageRef | undefined
        try {
            const mipLevelOffset = mipLevelOffsetIn ?? 0
            const sourceMipLevelRegions = determineSourceImageMipLevelRegion(sourceImage.descriptor, region, mipLevelOffset, mipLevelRegion)

            // if (TextureEditorSettings.EnableTests) {
            //     const newSourceMipLevelRegions = this.sourceMapping.getSourceMipLevelRegions({
            //         targetMipLevelRegionToWrite: mipLevelRegion,
            //         sourceMipLevel: mipLevelRegion.mipLevel,
            //     })
            //     const invalidatedTargetRegions = this.sourceMapping.computeTargetInvalidations(
            //         newSourceMipLevelRegions.map((mipLevelRegion) => mapRegionToMipLevel(mipLevelRegion, mipLevelRegion.mipLevel, 0)),
            //     )
            //     console.log("ExtractRegionHal writeRegion")
            //     console.log("Original mipLevelRegion", mipLevelRegion)
            //     console.log("Original source regions:", sourceMipLevelRegions)
            //     console.log("New source regions: ", newSourceMipLevelRegions)
            //     console.log("Invalidated target regions: ", invalidatedTargetRegions)
            //     console.log("Source mapping: ", this.sourceMapping.mappingBoundarySourceToTarget)
            //     console.log("Target mapping: ", this.sourceMapping.mappingBoundaryTargetToSource)
            // }

            sourceHalImage = await sourceImage.getUpdatedHalImage(sourceMipLevelRegions, options)
            // TODO currently we resample the image, but this might be avoidable once we support views with fractional region offsets/sizes
            const mipLevelScale = 1 / (1 << mipLevelRegion.mipLevel)
            const sourceRegion = region ?? {
                x: 0,
                y: 0,
                width: sourceImage.descriptor.width,
                height: sourceImage.descriptor.height,
            }
            let sourceOffset: Vector2Like
            if (isBox2Like(sourceRegion)) {
                updatedOffsetImage = undefined
                sourceOffset = {
                    x: sourceRegion.x * mipLevelScale,
                    y: sourceRegion.y * mipLevelScale,
                }
            } else {
                updatedOffsetImage = await sourceRegion.offsetImage.getUpdatedHalImage({
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                    mipLevel: 0,
                })
                sourceOffset = sourceRegion.offset
                    ? {
                          x: sourceRegion.offset.x * mipLevelScale,
                          y: sourceRegion.offset.y * mipLevelScale,
                      }
                    : {x: 0, y: 0}
            }
            const sourceMipLevel = Math.max(0, Math.min(sourceHalImage.halImage.descriptor.numMipLevels - 1, mipLevelRegion.mipLevel + mipLevelOffset))
            this.painter.ref.paint({
                target: outputImage,
                sourceImages: [sourceHalImage.halImage, updatedOffsetImage?.halImage],
                parameters: {
                    u_useOffsetImage: {type: "int", value: updatedOffsetImage ? 1 : 0},
                    u_sourceOffset: {type: "float2", value: sourceOffset},
                    u_sourceMipLevel: {type: "int", value: sourceMipLevel},
                    u_addressMode: {type: "int", value: getHalAddressMode(addressMode)},
                },
                options: {
                    targetRegion: mipLevelRegion,
                    targetMipLevel: mipLevelRegion.mipLevel,
                },
            })
        } finally {
            sourceHalImage?.release()
            updatedOffsetImage?.release()
            sourceImage.release("ExtractRegion.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        const targetRegions = determineTargetImageRegion(this.parameters.region, this.parameters.addressMode, sourceImage, sourceRegions)
        this.invalidateRegions(targetRegions)
    }

    private painter: HalPainterImageCompositorRef
}
